import React from 'react';

import styled from 'styled-components';

export interface EmbeddedViewerProps {
  /** URL to display in the iframe */
  url: string;
  /** Title for the embedded content */
  title: string;
  /** Custom className for styling */
  className?: string;
  /** Additional iframe attributes */
  allow?: string;
  /** Sandbox restrictions */
  sandbox?: string;
  /** Show header with title and URL info */
  showHeader?: boolean;
  /** Minimum height for the viewer */
  minHeight?: string;
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Callback when iframe loads */
  onLoad?: () => void;
  /** Callback when iframe fails to load */
  onError?: () => void;
}

/**
 * EmbeddedViewer - A reusable component for displaying external content via iframe
 *
 * This component is part of the AutoPanelLayout system and automatically handles:
 * - Responsive sizing and flex layout
 * - Security sandbox restrictions
 * - Loading states
 * - Error handling
 * - Header with content information
 *
 * Perfect for use in AutoPanelLayout panels without worrying about CSS.
 */
export const EmbeddedViewer: React.FC<EmbeddedViewerProps> = ({
  url,
  title,
  className,
  allow = 'fullscreen; clipboard-read; clipboard-write',
  sandbox = 'allow-scripts allow-same-origin allow-forms allow-popups',
  showHeader = true,
  minHeight = '300px',
  loadingComponent,
  onLoad,
  onError,
}) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);

  const handleLoad = React.useCallback(() => {
    setIsLoading(false);
    setHasError(false);
    onLoad?.();
  }, [onLoad]);

  const handleError = React.useCallback(() => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  }, [onError]);

  return (
    <Container className={className} $minHeight={minHeight}>
      {showHeader && (
        <Header>
          <HeaderTitle>{title}</HeaderTitle>
          <HeaderUrl>{url}</HeaderUrl>
        </Header>
      )}

      <ContentWrapper>
        {isLoading && <LoadingOverlay>{loadingComponent || <DefaultLoading>Loading content...</DefaultLoading>}</LoadingOverlay>}

        {hasError && (
          <ErrorOverlay>
            <ErrorMessage>
              <strong>Failed to load content</strong>
              <p>The external content could not be loaded. Please check the URL and try again.</p>
              <small>{url}</small>
            </ErrorMessage>
          </ErrorOverlay>
        )}

        <StyledIframe src={url} title={title} allow={allow} sandbox={sandbox} onLoad={handleLoad} onError={handleError} $isHidden={isLoading || hasError} />
      </ContentWrapper>
    </Container>
  );
};

// Styled Components with automatic flex layout
const Container = styled.div<{ $minHeight: string }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: ${({ $minHeight }) => $minHeight};
  border-radius: ${({ theme }) => theme.radii.base};
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: ${({ theme }) => theme.colors.palette.white};
`;

const Header = styled.div`
  flex-shrink: 0;
  padding: ${({ theme }) => theme.space.spacing10} ${({ theme }) => theme.space.spacing20};
  background: ${({ theme }) => theme.colors.palette.grayscale[1]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
`;

const HeaderTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.space.spacing10} 0;
  font-size: ${({ theme }) => theme.fontSizes.subheading};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const HeaderUrl = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.footnote};
  color: ${({ theme }) => theme.colors.textSecondary};
  word-break: break-all;
  font-family: ${({ theme }) => theme.fontFamilies.code || 'monospace'};
`;

const ContentWrapper = styled.div`
  flex: 1;
  position: relative;
  min-height: 0;
`;

const StyledIframe = styled.iframe<{ $isHidden: boolean }>`
  width: 100%;
  height: 100%;
  border: none;
  display: ${({ $isHidden }) => ($isHidden ? 'none' : 'block')};
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.palette.grayscale[0]};
  z-index: 1;
`;

const DefaultLoading = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
  font-size: ${({ theme }) => theme.fontSizes.body};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.palette.grayscale[0]};
  z-index: 1;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.space.spacing20};
  max-width: 400px;

  strong {
    display: block;
    margin-bottom: ${({ theme }) => theme.space.spacing10};
    font-size: ${({ theme }) => theme.fontSizes.subheading};
    color: ${({ theme }) => theme.colors.error || '#dc3545'};
  }

  p {
    margin: 0 0 ${({ theme }) => theme.space.spacing10} 0;
    font-size: ${({ theme }) => theme.fontSizes.body};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  small {
    font-size: ${({ theme }) => theme.fontSizes.footnote};
    color: ${({ theme }) => theme.colors.textSecondary};
    word-break: break-all;
    font-family: ${({ theme }) => theme.fontFamilies.code || 'monospace'};
  }
`;

EmbeddedViewer.displayName = 'EmbeddedViewer';
